import type { RegistryGiftBasketItemsByIdsQuery, RegistryGiftBasketOrdersByIdsQuery } from '@graphql/generated';
import { CustomItemDataType } from '@graphql/generated';
import type { LineItemStatus, OrderProductSummary, ProcessedDataPoints, RegistryOrders } from './GiftBasketContent.types';
import { notNullOrUndefined } from '@shared/utils/notNullOrUndefined';
import { flattenedValidRegistryOrders } from '../../RegistryGiftBasketWidget.utils';
import type { CurrencyFormatterArgs } from '@shared/utils/currency/types';

export const processRegistryItems = (
  data: Maybe<RegistryGiftBasketItemsByIdsQuery>,
  formatCurrency: (args: CurrencyFormatterArgs) => string,
  totalRegistryItemsCount: number,
): ProcessedDataPoints => {
  const items = (data?.registryItemsByIds || []).filter(notNullOrUndefined);
  const productRowDataProps = items.map(item => {
    const { donationFund, totalRequested } = item;
    const { title, photos, customType, price } = item.productData;

    const isCashFund = customType === CustomItemDataType.cash;
    const isCharityFund = customType === CustomItemDataType.charity;

    let eyebrow: Maybe<string> = undefined;
    let hed: string = title;
    let dek: Maybe<string> = undefined;
    if ((isCashFund || isCharityFund) && donationFund && donationFund.goalMonetaryValue) {
      eyebrow = isCashFund ? 'Cash Fund' : 'Charity Fund';
      dek = formatCurrency({
        priceFloatingPointDecimalString: donationFund.goalMonetaryValue.floatingPointDecimalString,
        priceCurrencyCode: donationFund.currency.code,
        formatForm: 'short',
        shouldCalculateCharmPrice: false,
      });
    } else {
      hed = notNullOrUndefined(totalRequested) && totalRequested > 1 ? `${totalRequested} x ${title}` : title;
      if (price) {
        dek = formatCurrency({
          priceFloatingPointDecimalString: price.floatingPointDecimalString,
          priceCurrencyCode: price.currency?.code,
          formatForm: 'short',
          shouldCalculateCharmPrice: true,
        });
      }
    }
    return {
      key: item.id,
      eyebrow,
      hed,
      dek,
      photoUrl: photos[0]?.url,
    };
  });
  return { productRowDataProps, overflowCount: totalRegistryItemsCount - items.length };
};

const getOrderLineItemStatus = (order: RegistryOrders[number], customType: OrderProductSummary['customType']): LineItemStatus => {
  if (order.isDropship) {
    return order.fulfillment?.status === 'shipped' ? 'shipped' : 'processing';
  } else if (customType === 'cash' || customType === 'charity') {
    return order.isPurchased ? 'given' : 'reserved';
  } else {
    return order.isPurchased ? 'purchased' : 'reserved';
  }
};

export const processRegistryOrders = (
  data: Maybe<RegistryGiftBasketOrdersByIdsQuery>,
  formatCurrency: (args: CurrencyFormatterArgs) => string,
  totalTrackedGiftsCount: number,
): ProcessedDataPoints => {
  const orders = (data?.registryOrdersByIds || []).filter(notNullOrUndefined);
  const validOrderWithLineItems = flattenedValidRegistryOrders(orders);
  const productRowDataProps =
    validOrderWithLineItems.entries.map(({ order, lineItem }) => {
      const { customType, price, title, photos } = lineItem.frozenProductData;
      const isCashFund = customType === CustomItemDataType.cash;
      const isCharityFund = customType === CustomItemDataType.charity;

      let dek: string;
      let eyebrow: Maybe<string> = undefined;
      if (isCashFund || isCharityFund) {
        dek = formatCurrency({
          priceFloatingPointDecimalString: price.floatingPointDecimalString,
          priceCurrencyCode: price.currency?.code,
          formatForm: 'short',
          shouldCalculateCharmPrice: false,
        });
        eyebrow = eyebrow = isCashFund ? 'Cash Fund' : 'Charity Fund';
      } else {
        dek = `Qty. ${lineItem.quantity}`;
      }

      return {
        key: lineItem.locator,
        eyebrow,
        hed: title,
        dek,
        photoUrl: photos[0]?.url,
        status: getOrderLineItemStatus(order, customType),
      };
    }) || [];

  const overflowCount = totalTrackedGiftsCount - validOrderWithLineItems.entries.length;
  return { overflowCount, productRowDataProps };
};
